import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  ListUsersService,
  mapEngagementLookbooksToLegacy,
  mapEngagementLookbookToLegacy,
} from '@epicuro-next/data/engagement/engagement-api-utils';
import {
  EngagementLookbooksWithUser,
  EngagementLookbookWithUser,
  ENGAGEMENT_API_TOKEN,
  LookbookAddRequestDto,
  LookBookGetRequestDto,
  LookbookResponseDto,
  LookbookResponseDtoPageResponseDto,
  LookBooksGetRequestDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { LookBook } from '@epicuro-next/models/engagement';
import { Observable, throwError } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import {
  extractLookbookUniqueLists,
  extractProductListsFromItems,
  mapLookbooksListsAvailability,
  mapSingleLookBookListsAvailability,
} from '../../utils';
import { ProductsAvailabilityApi } from '../products-availability-api/products-availability-api.service';

@Injectable()
export class LookbookApiService {
  private resource: string;
  constructor(
    private http: HttpClient,
    private listUsersService: ListUsersService,
    private productsAvailabilityApi: ProductsAvailabilityApi,
    @Inject(ENGAGEMENT_API_TOKEN) private readonly baseUrl: string,
  ) {
    this.resource = `${this.baseUrl}/lookbook`;
  }

  public getAllLookBooks(
    params: LookBooksGetRequestDto,
  ): Observable<EngagementLookbooksWithUser> {
    return this.http
      .get<LookbookResponseDtoPageResponseDto>(`${this.resource}`, {
        params: params?.queryparams,
      })
      .pipe(
        map((resp) => ({
          total: resp.total,
          items:
            resp.items?.map((item) =>
              mapEngagementLookbookToLegacy(item, []),
            ) ?? [],
        })),
        switchMap(({ total, items: lookBooks }) => {
          const uniqueLookBookLists = extractLookbookUniqueLists(lookBooks);

          const payload = {
            lists: uniqueLookBookLists,
            accountId: params.accountId ?? '',
            siteId: params?.siteId ?? '',
          };

          const userIds = lookBooks.map(
            ({ createdByUserId }) => createdByUserId,
          );

          return this.listUsersService
            .loadAndWaitForUserEntities(lookBooks, userIds)
            .pipe(
              first(({ userEntities }) => userEntities.length > 0),
              map(({ userEntities }) =>
                mapEngagementLookbooksToLegacy(lookBooks, userEntities),
              ),
              switchMap((lookBooksWithUser) => {
                return this.productsAvailabilityApi
                  .loadAvailabilities(payload)
                  .pipe(
                    map((listsWithAvailability) => {
                      const items = mapLookbooksListsAvailability(
                        lookBooksWithUser,
                        listsWithAvailability,
                      );
                      return { total, items };
                    }),
                  );
              }),
            );
        }),
      );
  }

  public getLookBook(payload: LookBookGetRequestDto) {
    const request$ = this.http.get<LookbookResponseDto>(
      `${this.resource}/${payload.queryparams.lookBookId}`,
    );

    return this.lookbookWithUsers(
      payload.accountId ?? '',
      payload?.siteId ?? '',
      request$,
    );
  }

  public createLookBook(payload: LookbookAddRequestDto) {
    const request$ = this.http.post<LookbookResponseDto>(`${this.resource}`, {
      title: payload.title,
      lookBookItems: payload.lookBookItems.map((i) => ({
        productListId: i.productListId,
      })),
    });

    return this.lookbookWithUsers(
      payload?.accountId ?? '',
      payload?.siteId ?? '',
      request$,
    );
  }

  public editLookBook(payload: Partial<LookBook>) {
    if (!payload.id)
      return throwError(
        () => new Error('Lookbook to edit, is not created yet'),
      );

    return this.http.put<void>(`${this.resource}/${payload.id}`, payload);
  }

  public deleteLookBook(id: string) {
    if (!id)
      return throwError(() => new Error('No id provided to delete lookbook'));

    return this.http.delete<void>(`${this.resource}/${id}`);
  }

  private lookbookWithUsers<T extends LookbookResponseDto>(
    accountId: string,
    siteId: string,
    request$: Observable<T>,
  ) {
    return request$.pipe(
      switchMap((resp) =>
        this.listUsersService
          .loadAndWaitForUserEntities(resp, [resp.createdByUserId])
          .pipe(first(({ userEntities }) => userEntities.length > 0)),
      ),
      map(({ resp, userEntities }) =>
        mapEngagementLookbookToLegacy(resp, userEntities),
      ),
      switchMap((lookBook) => {
        const lists = extractProductListsFromItems(lookBook.items);
        const payload = {
          lists,
          accountId,
          siteId,
        };

        return this.productsAvailabilityApi
          .loadAvailabilities(payload)
          .pipe(
            map(
              (listsWithAvailability) =>
                mapSingleLookBookListsAvailability(
                  lookBook,
                  listsWithAvailability,
                ) as EngagementLookbookWithUser,
            ),
          );
      }),
    );
  }
}
